import React, { useEffect, useState, useCallback } from 'react';
import { webhooksApi, WebhookEndpoint, WebhookDelivery } from '../../api/webhooks';

const ALL_EVENTS = [
  { value: 'call.started', label: 'Call Started', desc: 'Fired when an inbound or outbound call begins' },
  { value: 'call.completed', label: 'Call Completed', desc: 'Fired when a call ends successfully' },
  { value: 'call.failed', label: 'Call Failed', desc: 'Fired when a call fails, drops, or is rejected' },
  { value: 'campaign.launched', label: 'Campaign Launched', desc: 'Fired when a campaign starts running' },
  { value: 'campaign.completed', label: 'Campaign Completed', desc: 'Fired when all campaign calls finish' },
  { value: 'contact.opted_out', label: 'Contact Opted Out', desc: 'Fired when a contact requests removal from calls' },
  { value: 'research.study_completed', label: 'Research Study Completed', desc: 'All research interviews collected' },
  { value: 'research.report_ready', label: 'Research Report Ready', desc: 'AI analysis report has been generated' },
  { value: 'billing.usage_alert', label: 'Usage Alert', desc: 'Minute usage has reached 90% of plan limit' },
  { value: 'billing.payment_succeeded', label: 'Payment Succeeded', desc: 'Subscription payment processed successfully' },
  { value: 'billing.payment_failed', label: 'Payment Failed', desc: 'Subscription payment could not be processed' },
];

// ── Slide-over form ──────────────────────────────────────────────────────────
interface EndpointFormProps {
  initial?: Partial<WebhookEndpoint>;
  onSave: (data: { url: string; events: string[]; description?: string }) => Promise<void>;
  onClose: () => void;
}

const EndpointForm: React.FC<EndpointFormProps> = ({ initial, onSave, onClose }) => {
  const [url, setUrl] = useState(initial?.url || '');
  const [desc, setDesc] = useState(initial?.description || '');
  const [events, setEvents] = useState<string[]>(initial?.events || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (ev: string) =>
    setEvents((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));

  const handleSave = async () => {
    if (!url.trim()) { setError('URL is required'); return; }
    if (events.length === 0) { setError('Select at least one event'); return; }
    setSaving(true);
    try {
      await onSave({ url: url.trim(), events, ...(desc.trim() ? { description: desc.trim() } : {}) });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {initial?.id ? 'Edit Endpoint' : 'Add Endpoint'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="rounded bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Endpoint URL *
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-server.com/webhook"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="e.g. CRM integration"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Events to subscribe *
            </label>
            <div className="space-y-2">
              {ALL_EVENTS.map((ev) => (
                <label key={ev.value} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={events.includes(ev.value)}
                    onChange={() => toggle(ev.value)}
                    className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{ev.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{ev.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Endpoint'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Payload modal ────────────────────────────────────────────────────────────
const PayloadModal: React.FC<{ payload: object; onClose: () => void }> = ({ payload, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl flex flex-col">
      <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">Payload</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
      </div>
      <pre className="p-4 overflow-auto text-xs font-mono text-green-400 bg-gray-900 rounded-b-2xl flex-1">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </div>
  </div>
);

// ── Delivery log row ─────────────────────────────────────────────────────────
const DeliveryRow: React.FC<{ d: WebhookDelivery }> = ({ d }) => {
  const [showPayload, setShowPayload] = useState(false);

  return (
    <>
      {showPayload && <PayloadModal payload={d.payload} onClose={() => setShowPayload(false)} />}
      <tr className="border-b dark:border-gray-700 last:border-0">
        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 font-mono">{d.event_type}</td>
        <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
          {d.last_attempted_at ? new Date(d.last_attempted_at).toLocaleString() : '—'}
        </td>
        <td className="px-3 py-2 text-xs">
          {d.status_code ? (
            <span className={`px-2 py-0.5 rounded-full font-mono ${d.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {d.status_code}
            </span>
          ) : '—'}
        </td>
        <td className="px-3 py-2 text-xs">
          {d.success
            ? <span className="text-green-600 font-medium">✓ delivered</span>
            : <span className="text-red-500 font-medium">✗ failed</span>}
        </td>
        <td className="px-3 py-2 text-xs">
          <button
            onClick={() => setShowPayload(true)}
            className="text-blue-600 hover:underline"
          >
            View
          </button>
        </td>
      </tr>
    </>
  );
};

// ── Endpoint row ─────────────────────────────────────────────────────────────
interface EndpointRowProps {
  ep: WebhookEndpoint;
  onEdit: () => void;
  onDelete: () => void;
}

const EndpointRow: React.FC<EndpointRowProps> = ({ ep, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; statusCode: number; responseTime: number } | null>(null);
  const [testing, setTesting] = useState(false);

  const loadDeliveries = useCallback(async () => {
    setLoadingDeliveries(true);
    try {
      const res = await webhooksApi.deliveries(ep.id);
      setDeliveries(res.data.data);
    } catch { /* silent */ }
    finally { setLoadingDeliveries(false); }
  }, [ep.id]);

  const handleExpand = () => {
    if (!expanded) loadDeliveries();
    setExpanded((v) => !v);
  };

  const handleTest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setTesting(true);
    setTestResult(null);
    try {
      const res = await webhooksApi.test(ep.id);
      setTestResult(res.data.data);
    } catch { /* silent */ }
    finally { setTesting(false); }
  };

  return (
    <>
      <tr
        className="border-b dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        onClick={handleExpand}
      >
        <td className="px-4 py-3 text-sm font-mono text-blue-600 dark:text-blue-400 max-w-[200px] truncate">{ep.url}</td>
        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
          {ep.events.length > 0
            ? ep.events.slice(0, 3).join(', ') + (ep.events.length > 3 ? ` +${ep.events.length - 3}` : '')
            : '—'}
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ep.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {ep.active ? 'Active' : 'Inactive'}
          </span>
        </td>
        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{ep.description || '—'}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleTest}
              disabled={testing}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              title="Send test event"
            >
              {testing ? '⏳' : '▶ Test'}
            </button>
            <button
              onClick={onEdit}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Edit
            </button>
            <button
              onClick={onDelete}
              className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {testResult && (
        <tr className="bg-gray-50 dark:bg-gray-700/30">
          <td colSpan={5} className="px-4 py-2 text-xs">
            <span className={`font-medium ${testResult.success ? 'text-green-600' : 'text-red-500'}`}>
              {testResult.success ? '✓ Test succeeded' : '✗ Test failed'}
            </span>
            {' — '}{testResult.statusCode ?? 'no response'} · {testResult.responseTime}ms
          </td>
        </tr>
      )}
      {expanded && (
        <tr className="bg-gray-50 dark:bg-gray-800/50">
          <td colSpan={5} className="px-4 pb-4 pt-2">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
              Delivery Log (last 50)
            </div>
            {loadingDeliveries ? (
              <div className="text-xs text-gray-400 py-2">Loading...</div>
            ) : deliveries.length === 0 ? (
              <div className="text-xs text-gray-400 py-2">No deliveries yet.</div>
            ) : (
              <table className="w-full text-left rounded overflow-hidden">
                <thead>
                  <tr className="text-xs text-gray-400 border-b dark:border-gray-700">
                    <th className="px-3 pb-1">Event</th>
                    <th className="px-3 pb-1">Time</th>
                    <th className="px-3 pb-1">Status</th>
                    <th className="px-3 pb-1">Result</th>
                    <th className="px-3 pb-1">Payload</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => <DeliveryRow key={d.id} d={d} />)}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
export const WebhookSettings: React.FC = () => {
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<WebhookEndpoint | null>(null);
  const [apiKeyInfo, setApiKeyInfo] = useState<{ hasKey: boolean; maskedKey: string | null }>({ hasKey: false, maskedKey: null });
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadEndpoints = useCallback(async () => {
    try {
      const res = await webhooksApi.list();
      setEndpoints(res.data.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const loadApiKey = useCallback(async () => {
    try {
      const res = await webhooksApi.getApiKey();
      setApiKeyInfo(res.data.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadEndpoints();
    loadApiKey();
  }, [loadEndpoints, loadApiKey]);

  const handleCreate = async (data: { url: string; events: string[]; description?: string }) => {
    const res = await webhooksApi.create(data);
    setEndpoints((prev) => [res.data.data, ...prev]);
  };

  const handleUpdate = async (data: { url: string; events: string[]; description?: string }) => {
    if (!editTarget) return;
    const res = await webhooksApi.update(editTarget.id, data);
    setEndpoints((prev) => prev.map((ep) => ep.id === editTarget.id ? res.data.data : ep));
    setEditTarget(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this webhook endpoint?')) return;
    await webhooksApi.delete(id);
    setEndpoints((prev) => prev.filter((ep) => ep.id !== id));
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Regenerate API key? Your old key will stop working immediately.')) return;
    setRegenerating(true);
    try {
      const res = await webhooksApi.regenerateApiKey();
      setNewApiKey(res.data.data.apiKey);
      setApiKeyInfo({ hasKey: true, maskedKey: res.data.data.maskedKey });
    } catch { /* silent */ }
    finally { setRegenerating(false); }
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Webhooks & Integrations</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Push real-time events to your CRM, Slack, or any HTTP endpoint.
        </p>
      </div>

      {/* Endpoints section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Endpoints</h2>
          <button
            onClick={() => { setEditTarget(null); setShowForm(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            + Add Endpoint
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-400 py-4">Loading...</div>
        ) : endpoints.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 py-12 text-center">
            <div className="text-4xl mb-3">🔗</div>
            <p className="text-sm text-gray-500 dark:text-gray-400">No webhook endpoints yet.</p>
            <button
              onClick={() => { setEditTarget(null); setShowForm(true); }}
              className="mt-3 text-sm text-blue-600 hover:underline"
            >
              Add your first endpoint
            </button>
          </div>
        ) : (
          <div className="rounded-xl border dark:border-gray-700 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">URL</th>
                  <th className="px-4 py-3">Events</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.map((ep) => (
                  <EndpointRow
                    key={ep.id}
                    ep={ep}
                    onEdit={() => { setEditTarget(ep); setShowForm(true); }}
                    onDelete={() => handleDelete(ep.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* API Keys section */}
      <section className="rounded-xl border dark:border-gray-700 p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">API Key</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Use your API key to authenticate with the CallPilot API and Zapier.
          </p>
        </div>

        {newApiKey ? (
          <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <p className="text-sm text-green-700 dark:text-green-400 font-medium mb-2">
              ✓ New API key generated — copy it now, it won't be shown again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm bg-white dark:bg-gray-900 border border-green-300 dark:border-green-700 rounded px-3 py-2 text-gray-900 dark:text-green-300 select-all">
                {newApiKey}
              </code>
              <button
                onClick={() => copyKey(newApiKey)}
                className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <code className="flex-1 font-mono text-sm bg-gray-100 dark:bg-gray-700 rounded px-3 py-2 text-gray-700 dark:text-gray-300">
              {apiKeyInfo.maskedKey || (apiKeyInfo.hasKey ? '(hidden)' : 'No API key yet')}
            </code>
            {apiKeyInfo.maskedKey && (
              <button
                onClick={() => {/* can't copy masked key */}}
                disabled
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-400 cursor-not-allowed"
                title="Regenerate to get the full key"
              >
                Copy
              </button>
            )}
          </div>
        )}

        <button
          onClick={handleRegenerateKey}
          disabled={regenerating}
          className="text-sm px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
        >
          {regenerating ? 'Regenerating...' : apiKeyInfo.hasKey ? '⚠ Regenerate Key (old key revoked)' : 'Generate API Key'}
        </button>

        {/* Code example */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
            Example — authenticating with the API
          </p>
          <pre className="rounded-lg bg-gray-900 text-green-400 text-xs p-4 overflow-x-auto font-mono">
{`curl https://api.callpilot.ai/api/v1/zapier/auth/test \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          </pre>
        </div>
      </section>

      {/* Zapier section */}
      <section className="rounded-xl border dark:border-gray-700 p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Zapier</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Connect CallPilot to 5,000+ apps via Zapier — no code required.
          </p>
        </div>

        <a
          href="https://zapier.com/apps/callpilot"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600"
        >
          <span>⚡</span> Connect with Zapier
        </a>

        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4 text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <p className="font-medium text-gray-700 dark:text-gray-200 mb-2">How to connect:</p>
          <p>1. Click <strong>Connect with Zapier</strong> above</p>
          <p>2. Search for <strong>CallPilot</strong> in the Zapier app directory</p>
          <p>3. Authenticate using your API key (from the section above)</p>
          <p>4. Choose a trigger event and build your Zap</p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Available triggers:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ALL_EVENTS.map((ev) => (
              <div
                key={ev.value}
                className="rounded-lg border dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
              >
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{ev.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ev.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Slide-over form */}
      {showForm && editTarget && (
        <EndpointForm
          initial={editTarget}
          onSave={handleUpdate}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
        />
      )}
      {showForm && !editTarget && (
        <EndpointForm
          onSave={handleCreate}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
        />
      )}
    </div>
  );
};

export default WebhookSettings;
